/**
 * Funzone visitor tracking:
 * - Uses a unique token stored in localStorage (no expiry).
 * - Calls the backend POST API only when the token is missing.
 * - Token: timestamp + randomness for uniqueness.
 */

const STORAGE_KEY = "funzone_visitor_token";
const SOURCE = "funzone-web";

function getApiBaseUrl() {
  return process.env.REACT_APP_API_URL || "";
}

/**
 * Generate a unique token: current timestamp + random string.
 * @returns {string}
 */
export function generateVisitorToken() {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 15);
  return `${timestamp}-${random}`;
}

/**
 * Get existing visitor token from localStorage, or null if not set.
 * @returns {string|null}
 */
export function getVisitorToken() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Store visitor token in localStorage (persists until user clears storage).
 * @param {string} token
 */
export function setVisitorToken(token) {
  try {
    localStorage.setItem(STORAGE_KEY, token);
  } catch (_) {
    // ignore quota or private mode
  }
}

/**
 * Call the backend to record the visitor. Only call when token was missing and we just created one.
 * Uses sourceId = token, source = 'funzone-web', userAgent from navigator.
 * @param {string} sourceId - Unique token for this browser
 */
export async function recordVisitor(sourceId) {
  const baseUrl = getApiBaseUrl();
  if (!baseUrl) return;

  const url = `${baseUrl}/funzone/visitor`;
  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sourceId, source: SOURCE, userAgent }),
    });
    if (!res.ok) {
      console.warn("Funzone visitor record failed:", res.status);
    }
  } catch (err) {
    console.warn("Funzone visitor request failed:", err);
  }
}

/**
 * Ensure we have a visitor token; if not, create one, store it, and call the API.
 * Call this once when the app loads.
 */
export async function ensureVisitorRecorded() {
  let token = getVisitorToken();
  if (!token) {
    token = generateVisitorToken();
    setVisitorToken(token);
    await recordVisitor(token);
  }
}
